/**
 * accountFromBooking (Jose 2026-10-12) — turn a guest booking into a real SLIK account in one tap.
 *
 * The public-booking page is already signed in ANONYMOUSLY, so we LINK that session to an email/password
 * (same uid → the appointment they're booking is instantly owned by their new account). Falls back to a
 * fresh create, and to signing into an existing account if the email is already registered.
 *
 * NEVER throws — returns {created} so the booking ALWAYS proceeds regardless of the account outcome.
 * Writes the same users + clients profile shape the customer portal's signUpWithEmail uses, so a
 * Book-Now account == a portal account == the app account (same login everywhere). Any profile-write
 * failure is swallowed: getCustomerProfile auto-heals the docs on first portal login.
 */
import { auth, db } from '../config/firebase';
import {
  EmailAuthProvider, linkWithCredential, signInWithEmailAndPassword,
  createUserWithEmailAndPassword, updateProfile,
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';

export interface AccountResult {
  created: boolean;
  reason?: 'new' | 'existing' | 'exists-wrong-password' | 'error';
}

function splitName(full: string) {
  const parts = (full || '').trim().split(/\s+/);
  return { firstName: parts[0] || '', lastName: parts.slice(1).join(' ') };
}

async function writeProfile(uid: string, email: string, phone: string, firstName: string, lastName: string) {
  const now = new Date();
  // merge:true so we never clobber an existing richer record (e.g. a returning client).
  await setDoc(doc(db, 'users', uid), {
    uid, email, phone, firstName, lastName, role: 'customer',
    createdAt: now, lastLogin: now, cpi: 0, totalAppointments: 0, totalSpent: 0,
    slikCoins: 0, loyaltyPoints: 0, tier: 'BRONZE', hasSeenFamilyPrompt: false,
  }, { merge: true });
  await setDoc(doc(db, 'clients', uid), {
    id: uid, userId: uid, firstName, lastName, email, phone,
    cpi: 0, totalAppointments: 0, totalSpent: 0, slikCoins: 0, loyaltyPoints: 0,
    tier: 'BRONZE', appointmentHistory: [], createdAt: now, lastVisit: null,
    status: 'active', notes: 'Created via Book Now',
  }, { merge: true });
}

export async function createAccountFromBooking(
  emailRaw: string, password: string, name: string, phone: string,
): Promise<AccountResult> {
  const email = (emailRaw || '').trim();
  const phoneClean = (phone || '').replace(/\D/g, '');
  const { firstName, lastName } = splitName(name);
  if (!email || !password) return { created: false, reason: 'error' };

  const cred = EmailAuthProvider.credential(email, password);
  try {
    if (auth.currentUser && auth.currentUser.isAnonymous) {
      await linkWithCredential(auth.currentUser, cred); // upgrade the anon session in place (same uid)
    } else {
      await createUserWithEmailAndPassword(auth, email, password);
    }
    const u = auth.currentUser;
    if (!u) return { created: false, reason: 'error' };
    try { await updateProfile(u, { displayName: (name || '').trim() }); } catch { /* non-fatal */ }
    try { await writeProfile(u.uid, email, phoneClean, firstName, lastName); } catch { /* auto-heals on login */ }
    return { created: true, reason: 'new' };
  } catch (e: any) {
    const code = String(e?.code || '');
    if (/already-in-use/.test(code)) {
      // The email already has an account — try to sign into it with the password they entered.
      try {
        await signInWithEmailAndPassword(auth, email, password);
        return { created: true, reason: 'existing' };
      } catch {
        return { created: false, reason: 'exists-wrong-password' };
      }
    }
    return { created: false, reason: 'error' };
  }
}
